// TESSERAX — JALONS DE SCORE (cœur pur, testé).
//
// Les grands nombres ronds sont un crochet d'arcade éprouvé : « passer la barre
// des 100 000 » est un évènement mémorable EN SOI, indépendant du record (relatif
// à TA meilleure marque) et du combo (interne à une résolution). Or le score
// grimpait en SILENCE entre deux clears — aucun seuil absolu n'était jamais
// célébré. On comble ce vide de retour : un palier franchi = un petit apogée.
//
// Module PUR : ni THREE ni DOM, pas de Math.random → l'évènement est purement de
// l'AFFICHAGE (aucun tirage altéré) ⇒ déterminisme du défi du jour intact.

// Paliers ronds « mémorables », STRICTEMENT croissants. Choisis pour rester
// FRANCHIS rarement (une poignée par longue partie) : trop fréquent = bruit qui
// fatigue, trop rare = muet. La progression géométrique (×2,5 / ×2) garde un
// espacement ressenti constant à mesure que le score enfle en profondeur.
export const MILESTONES = [
  10_000, 25_000, 50_000, 100_000, 250_000, 500_000,
  1_000_000, 2_500_000, 5_000_000, 10_000_000,
];

export interface Milestone {
  /** Valeur ronde franchie (ex. 100000). */
  value: number;
  /** Index dans MILESTONES (utile pour des tests / debug). */
  index: number;
  /** Palier d'intensité 1..3 pour piloter le juice sans dupliquer de table. */
  tier: number;
}

// Palier d'intensité d'un jalon, dérivé de son rang (pas d'une 2ᵉ table de seuils) :
//   10k · 25k · 50k         → 1 (discret : toast + son léger)
//   100k · 250k · 500k      → 2 (marqué : flash + son plein)
//   ≥ 1M                    → 3 (apogée : bandeau + cinématique)
export function tierFor(index: number): number {
  if (index <= 2) return 1;
  if (index <= 5) return 2;
  return 3;
}

// Renvoie le PLUS HAUT palier strictement franchi en passant de `prev` à `next`
// (condition de franchissement : prev < palier ≤ next), ou null si aucun.
//
// On ne renvoie QUE le sommet franchi : un gros clear qui saute plusieurs paliers
// d'un coup déclenche un seul évènement (« tu viens de passer la plus grosse
// barre »), jamais une rafale de toasts. `next === palier` compte (on atterrit
// pile dessus) ; `next <= prev` (score stable ou en recul) ne franchit jamais.
// Tolérant aux entrées non finies (NaN / Infinity → aucun jalon).
export function crossedMilestone(prev: number, next: number): Milestone | null {
  if (!Number.isFinite(prev) || !Number.isFinite(next) || next <= prev) return null;
  let hit: Milestone | null = null;
  for (let i = 0; i < MILESTONES.length; i++) {
    const v = MILESTONES[i];
    if (v > prev && v <= next) hit = { value: v, index: i, tier: tierFor(i) };
  }
  return hit;
}

// PROCHAIN palier STRICTEMENT au-dessus de `score` — sert d'objectif court terme
// PERMANENT en mode endless (RET-CORE-001), là où aucun but de ligne/temps n'existe.
// Renvoie null quand le score dépasse déjà le dernier palier (« sommet atteint »).
// Pur / sans effet de bord ⇒ déterminisme intact (pur affichage).
export function nextMilestone(score: number): Milestone | null {
  if (!Number.isFinite(score)) return null;
  for (let i = 0; i < MILESTONES.length; i++) {
    if (MILESTONES[i] > score) return { value: MILESTONES[i], index: i, tier: tierFor(i) };
  }
  return null;
}
